import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { TeamUsage, PlanLimits, LimitResourceType } from '../types/billing';
import { isAtLimit } from '../types/billing';

interface UsePlanLimitsOptions {
  /** Whether to enable the query */
  enabled?: boolean;
}

/**
 * Hook to fetch and manage plan limits for a team
 */
export function usePlanLimits(teamId: string | null, options: UsePlanLimitsOptions = {}) {
  const { enabled = true } = options;

  // Fetch team usage
  const usageQuery = useQuery({
    queryKey: ['team-usage', teamId],
    queryFn: async () => {
      if (!teamId) return null;
      const response = await apiClient.get<TeamUsage>(`/api/v1/teams/${teamId}/usage`);
      return response.data;
    },
    enabled: !!teamId && enabled,
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: true,
  });

  // Fetch plan limits
  const limitsQuery = useQuery({
    queryKey: ['team-limits', teamId],
    queryFn: async () => {
      if (!teamId) return null;
      const response = await apiClient.get<PlanLimits>(`/api/v1/teams/${teamId}/limits`);
      return response.data;
    },
    enabled: !!teamId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes (limits change less frequently)
  });

  const usage = usageQuery.data;

  // Helper functions
  const canCreateProject = (): boolean => {
    if (!usage) return true;
    return !isAtLimit(usage.projects);
  };

  const canCreateSecret = (): boolean => {
    if (!usage) return true;
    return !isAtLimit(usage.secrets);
  };

  const canInviteMember = (): boolean => {
    if (!usage) return true;
    return !isAtLimit(usage.members);
  };

  const canCreateEnvironment = (): boolean => {
    if (!usage) return true;
    return !isAtLimit(usage.environments);
  };

  const getResourceWarning = (type: LimitResourceType): boolean => {
    if (!usage) return false;
    return usage.warnings.includes(type);
  };

  const getResourceAtLimit = (type: LimitResourceType): boolean => {
    if (!usage) return false;
    return usage.at_limit.includes(type);
  };

  const hasAnyWarnings = (): boolean => {
    if (!usage) return false;
    return usage.warnings.length > 0;
  };

  const hasAnyAtLimit = (): boolean => {
    if (!usage) return false;
    return usage.at_limit.length > 0;
  };

  return {
    // Data
    usage,
    limits: limitsQuery.data,
    plan: usage?.plan ?? 'free',

    // Loading states
    isLoading: usageQuery.isLoading || limitsQuery.isLoading,
    isUsageLoading: usageQuery.isLoading,
    isLimitsLoading: limitsQuery.isLoading,

    // Errors
    error: usageQuery.error || limitsQuery.error,
    usageError: usageQuery.error,
    limitsError: limitsQuery.error,

    // Refetch
    refetch: () => {
      usageQuery.refetch();
      limitsQuery.refetch();
    },
    refetchUsage: usageQuery.refetch,

    // Can create checks
    canCreateProject,
    canCreateSecret,
    canInviteMember,
    canCreateEnvironment,

    // Warning checks
    getResourceWarning,
    getResourceAtLimit,
    hasAnyWarnings,
    hasAnyAtLimit,

    // Raw resource usage
    projectsUsage: usage?.projects,
    secretsUsage: usage?.secrets,
    membersUsage: usage?.members,
    environmentsUsage: usage?.environments,
  };
}

export type UsePlanLimitsReturn = ReturnType<typeof usePlanLimits>;
